/**
 * Builds the derived coverage view-models (`coverageInformation` structure)
 * from Postgres-sourced props.
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { CoverageInformation } from "../../../../data-model/coverageInformation";
import {
  CoverageResult,
  Demographics,
  Payload,
  SingleCoverage,
} from "../../../../data-model/medicalPaOrdersModel";
import { Submission } from "../../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { RootState } from "../../../../redux/store/store";
import { mapPostgresCoveragesToCoverageInformation } from "../../helpers/mapMedicalPaOrderToCoverageInformation";

interface UseCoverageDataResult {
  coverageInformation: CoverageInformation | null;
}

/**
 * Derivation hook returning the coverageInformation structure used by the tab
 * content. Returns `null` when there are no rows so the parent can
 * short-circuit to the no-data view.
 *
 * The facility-level Tax ID is read internally from the
 * `singleHealthFacilityDetails` Redux slice (`data.TaxID`) and forwarded to
 * `mapPostgresCoveragesToCoverageInformation`, where it takes
 * precedence over `providerTaxId` from the coverage request when
 * populating each tab's Provider Information block. The hook still
 * returns a memoized result, but the memo key intentionally excludes the
 * Redux-derived Tax ID — it is treated as a stable per-facility value.
 *
 * @param coverages - Coverage rows from Postgres
 * @param coverage_results - Coverage result rows aligned with `coverages`
 * @param demographicsData - Patient demographics from the order payload
 * @param payload - Order payload (service type, date of service, etc.)
 * @param submissionData - Order submission details supplying the requesting
 *   practitioner's first name, last name, and NPI for each coverage tab's
 *   Provider Information block
 * @returns Memoized `coverageInformation` for the UI
 */
export const useCoverageData = (
  coverages: SingleCoverage[] | undefined,
  coverage_results: CoverageResult[] | undefined,
  demographicsData: Demographics | undefined,
  payload: Payload | undefined,
  submissionData: Submission | undefined,
): UseCoverageDataResult => {
  const { data } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );
  return useMemo(() => {
    const coverageInformation = mapPostgresCoveragesToCoverageInformation(
      coverages ?? [],
      coverage_results ?? [],
      demographicsData,
      payload,
      submissionData,
      data?.TaxID ?? "",
    );

    if (!coverageInformation.length) {
      return { coverageInformation: null };
    }

    return { coverageInformation };
  }, [coverages, coverage_results, demographicsData, payload, submissionData]);
};
